import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { CloseShortButton } from "../orders/CloseShortButton";

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  confirmed: { cls: "b-info", label: "Onaylı" },
  in_production: { cls: "b-warn", label: "Üretimde" },
  ready: { cls: "b-success", label: "Sevke Hazır" },
  shipped: { cls: "b-success", label: "Sevk Edildi" },
  cancelled: { cls: "b-danger", label: "İptal" },
};
const KIND_TR: Record<string, string> = { stock: "Hammadde", semi: "Yarı Mamul", finished: "Mamul" };

const miniLbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase", color: "var(--neutral-500)", marginBottom: 3 };

/** Sevkiyata hazır siparişin SALT-OKUNUR detayı — kalemler, miktarlar, tarihler. Finansal veri (fiyat) YOK. */
export function ShippableOrderDetailDrawer({ open, orderId, onClose }: { open: boolean; orderId: string | null; onClose: () => void }) {
  const q = trpc.order.get.useQuery({ id: orderId! }, { enabled: open && !!orderId });
  // Sevkiyat geçmişi (İPTALLER DAHİL) — "sipariş sevk edildi sanılması" sorununun
  // kaynağı bu bilginin hiçbir ekranda olmamasıydı.
  const gecmisQ = trpc.shipment.orderHistory.useQuery({ orderId: orderId! }, { enabled: open && !!orderId });
  const gecmis = gecmisQ.data ?? [];
  const o = q.data;
  const sb = o ? (STATUS_BADGE[o.status] ?? { cls: "", label: o.status }) : null;
  const totalQty = o ? o.lines.reduce((s, l) => s + l.qty, 0) : 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={o ? `Sipariş ${o.code}` : "Sipariş Detayı"}
      subtitle="Sevkiyat öncesi salt-okunur özet — kalemler ve miktarlar"
      /* Özet salt-okunur ama TEK istisna burada: kullanıcı "sevk edilmiş sipariş
         hâlâ listede" sorununu TAM BU EKRANDA görüyor. Kapatma eylemini yalnız
         Siparişler sayfasına koymak, sorunu gördüğü yerden iki sayfa uzağa
         taşırdı. Buton yalnız kısmen sevk edilmiş siparişte görünür. */
      footer={<CloseShortButton order={o} onClosed={onClose} />}
    >
      {q.isLoading ? (
        <div style={{ color: "var(--neutral-500)", fontSize: 13, padding: 8 }}>Yükleniyor…</div>
      ) : q.isError ? (
        <div className="form-banner-err">{q.error?.message}</div>
      ) : o ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Üst şerit: durum + müşteri */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {sb ? <span className={`badge-pill ${sb.cls}`}>{sb.label}</span> : null}
            <span style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{o.orderType === "customer" ? "Müşteri siparişi" : "Stoğa üretim"}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)" }}>{o.customerName ?? "—"}</span>
            {o.customerCode ? <span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)" }}>{o.customerCode}</span> : null}
          </div>

          {/* Tarih alanları */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div><div style={miniLbl}>Sipariş Tarihi</div><div className="mono" style={{ fontSize: 13 }}>{fmtDate(o.createdAt)}</div></div>
            <div><div style={miniLbl}>İstenen Tarih</div><div className="mono" style={{ fontSize: 13 }}>{fmtDate(o.requestedDate)}</div></div>
            <div><div style={miniLbl}>Söz Verilen</div><div className="mono" style={{ fontSize: 13 }}>{fmtDate(o.promisedDate)}</div></div>
          </div>

          {o.notes ? (
            <div style={{ background: "var(--neutral-50)", borderRadius: "var(--radius-md)", padding: "8px 12px" }}>
              <div style={miniLbl}>Not</div>
              <div style={{ fontSize: 13, color: "var(--neutral-700)", whiteSpace: "pre-wrap" }}>{o.notes}</div>
            </div>
          ) : null}

          {/* Kalemler */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Sipariş Kalemleri</span>
              <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{o.lines.length} kalem · Σ {fmtQty(totalQty)}</span>
            </div>
            <table className="dt2">
              <thead>
                <tr>
                  <th>Ürün</th>
                  <th style={{ width: 70 }}>Tür</th>
                  <th className="num" style={{ width: 110 }}>Miktar</th>
                  <th className="num" style={{ width: 90 }}>Rezerve</th>
                  <th className="num" style={{ width: 90 }}>Sevk</th>
                </tr>
              </thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span></td>
                    <td><span style={{ fontSize: 9, fontWeight: 600, color: "var(--neutral-500)", background: "var(--neutral-100)", padding: "1px 4px", borderRadius: 3 }}>{KIND_TR[l.itemKind] ?? l.itemKind}</span></td>
                    <td className="cell-num" style={{ fontWeight: 600 }}>{fmtQty(l.qty)}<span className="unit">{l.uom}</span></td>
                    <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{fmtQty(l.reservedQty)}</td>
                    <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{fmtQty(l.shippedQty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── SEVKİYAT GEÇMİŞİ ── */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Sevkiyat Geçmişi</span>
              <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>
                {gecmis.length === 0 ? "hiç plan yapılmamış" : `${gecmis.length} kayıt`}
              </span>
            </div>
            {gecmis.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>Bu sipariş için henüz sevkiyat planı açılmamış.</div>
            ) : (
              <>
                {gecmis.some((g) => g.status === "cancelled") && !gecmis.some((g) => g.status === "confirmed") ? (
                  <div className="form-banner-warn" style={{ marginBottom: 8 }}>
                    <Icon name="alertTriangle" size={14} />
                    <div>
                      Bu sipariş için <b>{gecmis.filter((g) => g.status === "cancelled").length} plan açılıp iptal edilmiş</b>,
                      onaylanan sevkiyat <b>yok</b>. İptal edilen plan stok düşürmez — sipariş sevk edilmemiş sayılır.
                    </div>
                  </div>
                ) : null}
                <table className="dt2">
                  <thead>
                    <tr><th>Sevkiyat</th><th style={{ width: 96 }}>Durum</th><th className="num" style={{ width: 90 }}>Miktar</th><th style={{ width: 96 }}>Tarih</th><th>Gerekçe</th></tr>
                  </thead>
                  <tbody>
                    {gecmis.map((g) => (
                      <tr key={g.id}>
                        <td><span className="mono" style={{ fontSize: 11.5 }}>{g.code}</span></td>
                        <td>
                          <span className={`badge-pill ${g.status === "confirmed" ? "b-success" : g.status === "cancelled" ? "b-danger" : ""}`}>
                            {g.status === "confirmed" ? "Sevk Edildi" : g.status === "cancelled" ? "İptal" : "Hazırlanıyor"}
                          </span>
                        </td>
                        <td className="cell-num">{fmtQty(g.qty)}</td>
                        <td><span className="mono" style={{ fontSize: 11 }}>{fmtDate(g.shippedAt ?? g.cancelledAt ?? g.createdAt)}</span></td>
                        <td style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{g.cancelReason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--neutral-400)" }}>
            <Icon name="fileText" size={13} />Bu özet salt-okunurdur; sevkiyat planlamak için satırdaki "Sevkiyat Planla" butonunu kullanın.
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
